import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import classes from './LiveFeedback.module.less'
import { getLiveFeedbackWithAIApi, LiveFeedbackRequestType } from 'HTTPClients/plugins.client'
import { useMemo, useState } from 'react'
import { Tooltip } from 'antd/lib'
import { useMessage } from 'shared/hooks/use-message'
import { Loader } from 'Components/common/Loader/Loader'
import { LiveFeedbackModal } from 'Tasks/CustomTask/UI/common/LiveFeedback/LiveFeedbackModal'
import { CustomLiveFeedbackType } from 'Types/CustomTask.types'
import { LiveFeedbackContextType, LiveFeedbackPluginConfigType } from 'Types/LiveFeedback.types'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'

type LiveFeedbackPropsType = {
    modelResponse: string
    humanAnswer?: string
    humanReasoningAnswer?: string
    domain: string
    pluginId: string
    question: string
    setIsLiveFeedbackChecked: () => void
    isEnabled: boolean
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
    pluginConfig: PluginConfigType<LiveFeedbackPluginConfigType> | null
}

export const LiveFeedback = (props: LiveFeedbackPropsType) => {
    const [messageApi] = useMessage()
    const [isLoading, setIsLoading] = useState(false)
    const [liveFeedbackDetails, setLiveFeedbackDetails] = useState<CustomLiveFeedbackType | null>(
        null
    )
    const [checksMade, setChecksMade] = useState(props.pluginConfig?.usageStats ?? 0)

    const isPluginUsageLimitExceeded = useMemo(() => {
        const limit = props.pluginConfig?.config?.limit ?? Infinity
        return checksMade >= limit
    }, [checksMade, props.pluginConfig?.config?.limit])

    const loadingContent = useMemo(() => {
        if (!isLoading) return null
        return <Loader title="Loading... Please wait..." />
    }, [isLoading])

    const onClickFetchLiveFeedback = async () => {
        // Check validation right before making the API call
        try {
            const isValid = await props.areAttributesValid()
            if (!isValid) {
                messageApi.error('Please fill all required fields before getting live feedback')
                return
            }
        } catch (error) {
            console.error('Validation error:', error)
            messageApi.error('Please fill all required fields before getting live feedback')
            return
        }

        setIsLoading(true)

        // Get the latest container data (updated after prompt generation)
        const latestContainerData = props.liveFeedbackContext.getContainerData?.()

        if (!latestContainerData) {
            messageApi.error('Container data not available')
            setIsLoading(false)
            return
        }

        try {
            // Make the Live Feedback API call with the latest container
            const requestPayload: LiveFeedbackRequestType = {
                taskPluginId: props.pluginId,
                userId: props.liveFeedbackContext.userId,
                jobId: props.liveFeedbackContext.jobId,
                refId: props.liveFeedbackContext.refId,
                inputContainer: props.liveFeedbackContext.inputContainer,
                containerData: latestContainerData,
            }

            const resp = await getLiveFeedbackWithAIApi(requestPayload)
            if (resp.isOk()) {
                setLiveFeedbackDetails(resp.value.response.QC)
                setChecksMade(prev => prev + 1)
                messageApi.success('Live Feedback received successfully')
            } else {
                const errorMessage =
                    (resp.error.response?.data as { error: { message: string } })?.error?.message ??
                    'Something went wrong while getting live feedback'
                messageApi.error(errorMessage)
            }
        } catch (error) {
            console.error('Live Feedback Error:', error)
            messageApi.error('An unexpected error occurred')
        }

        setIsLoading(false)
        props.setIsLiveFeedbackChecked()
    }

    if (!props.isEnabled) {
        return null
    }

    return (
        <>
            <Tooltip
                title={
                    isPluginUsageLimitExceeded
                        ? 'You have reached the maximum limit of getting live feedback'
                        : ''
                }
            >
                <ActionButton
                    color="default"
                    onClick={onClickFetchLiveFeedback}
                    title="Get Live Feedback"
                    className={classes.scanButton}
                    disabled={
                        props.modelResponse.length === 0 ||
                        !props.humanAnswer ||
                        !props.humanReasoningAnswer ||
                        isPluginUsageLimitExceeded
                    }
                />
            </Tooltip>
            {loadingContent}
            <LiveFeedbackModal
                liveFeedbackDetails={liveFeedbackDetails}
                setLiveFeedbackDetails={setLiveFeedbackDetails}
            />
        </>
    )
}
